import Raven from 'raven-js';
import { NON_RECURRING, RECURRING } from 'self-service-api/constants/ChargeTypes';
import { QUANTITY_PACK } from 'self-service-api/constants/SkuTypes';
import { ANNUAL, MONTHLY, ONCE } from 'self-service-api/constants/Terms';
import { isCoreSeatSku } from 'self-service-api/core/utilities/seatUtils';
import { getFilteredProductConfigurationsByChargeType } from 'ui-addon-upgrades/_core/utils/productUtils';
import { OfferIdNotFoundError } from '../errors/CustomErrors';
import { getIsUngatedForSeats } from './gatingUtils';
import { getAllTermsInProductInventory, getCurrentTermFromProductInventory } from './productInventoryUtils';
import { getCrmSuiteProductLevelForProductConfigurations, getCrmSuiteProductLevelForSkuIdsPreSeatsAndSeatsOnly, getHubProductVersionSkuIdsOrDefault, getIsCrmSuiteSalesOrServiceSkuProEnt, getProductForSkuId, getProductVersion, getSkuIdIsAddonOrService, getSkuIdIsInCrmSuite, getSkuIdIsOrHasHub, getSkuIdsContainOnlyCRMSuite, getSkuUuidForSkuId } from './productsUtils';
import { getOffersMap, getProductPromotion, getPromotionForSkuId, hasUnbundledCrmSuiteOfferIdsInPromotions } from './promotionUtils';

/**
 * Sort offers such that lower terms come first
 * at time of writing, this means MCMP, ACMP, ACAP
 */
export const offerInfoSortingFunc = (firstInfo, secondInfo) => {
  if (!firstInfo || !secondInfo) return 0;
  if (firstInfo.payment === MONTHLY && secondInfo.payment === ANNUAL || firstInfo.commitment === MONTHLY && secondInfo.commitment === ANNUAL) {
    return -1;
  }
  return 1;
};
export const sortOfferIds = (offersById, offerIds) => {
  return [...offerIds].sort((firstOfferId, secondOfferId) => {
    return offerInfoSortingFunc(offersById[firstOfferId], offersById[secondOfferId]);
  });
};
const reportedOfferInfoErrors = [];
export const getOfferInfo = ({
  promotions
}, {
  offerId
}) => {
  if (!offerId || !promotions || !promotions.offersById) return null;
  if (!(offerId in promotions.offersById)) {
    if (!reportedOfferInfoErrors.includes(offerId)) {
      reportedOfferInfoErrors.push(offerId);
      console.error('Called getOfferInfo with unknown offer id', {
        offerId,
        offersMap: promotions.offersById
      });
      throw new Error(`Called getOfferInfo with unknown offer id ${offerId}`);
    }
  }
  return promotions.offersById[offerId];
};
export const getMultipleOfferInfo = (state, {
  offerIds
}) => {
  return offerIds.map(offerId => getOfferInfo(state, {
    offerId
  })).filter(offerInfo => offerInfo).sort(offerInfoSortingFunc);
};
export const getAvailableOfferIdsForPromotion = ({
  promotions
}, {
  promotion,
  limitToTwo = true
}) => {
  const {
    offerIds
  } = promotion;
  const newOfferIds = [...offerIds];
  if (limitToTwo && offerIds && offerIds.length > 2) {
    newOfferIds.splice(2, 1);
  }
  return (newOfferIds || []).sort((firstOfferId, secondOfferId) => {
    return offerInfoSortingFunc(promotions.offersById[firstOfferId], promotions.offersById[secondOfferId]);
  });
};
export const findOfferIdForTerm = ({
  promotions
}, {
  offerIds,
  offerTerms
}) => {
  return offerIds.find(offerId => {
    const offerInfo = getOfferInfo({
      promotions
    }, {
      offerId
    });
    return offerInfo && 'commitment' in offerTerms && 'payment' in offerTerms && offerInfo.commitment === offerTerms.commitment && offerInfo.payment === offerTerms.payment;
  });
};
export const getAvailableOfferIdsForSku = (state, {
  skuId,
  limitToTwo
}) => {
  const promotion = getPromotionForSkuId(state, {
    skuId
  });
  return getAvailableOfferIdsForPromotion(state, {
    promotion,
    limitToTwo
  });
};
export const getProductBasedOfferId = (state, {
  skuId
}) => {
  const promotion = getPromotionForSkuId(state, {
    skuId
  });
  const offerIds = getAvailableOfferIdsForPromotion(state, {
    promotion,
    limitToTwo: false
  });
  return offerIds.find(offerId => {
    const offerInfo = getOfferInfo(state, {
      offerId
    });
    return offerInfo && offerInfo.payment === ONCE;
  });
};

// Returns a list of terms available for a sku
// limitToTwoTerms will remove ACMP if all three terms are valid
// (which can happen for add-ons)
/**
 * @deprecated Please use one of the POP hooks in usePurchaseOptions.ts or useBulkPurchaseOptions.ts
 */
export const getAvailableTermsForSku = (state, {
  skuId,
  limitToTwo
}) => {
  return getMultipleOfferInfo(state, {
    offerIds: getAvailableOfferIdsForSku(state, {
      skuId,
      limitToTwo
    })
  });
};
export const getProductVersionDoesntAllowDiscount = (state, {
  skuId
}) => {
  const product = getProductForSkuId(state, {
    skuId
  });
  if (!product) return null;
  const {
    chargeType
  } = product;
  const isRecurringMerch = chargeType === RECURRING;
  return !isRecurringMerch;
};

// Takes in a SKU id, terms object, and optionally fallback terms
// in case the requested term isn't available.
// Returns the offer id
export const getOfferId = (state, {
  skuId,
  terms,
  fallbackTerms = []
}) => {
  if (getProductVersionDoesntAllowDiscount(state, {
    skuId
  })) {
    const productBasedOffer = getProductBasedOfferId(state, {
      skuId
    });
    if (productBasedOffer) return productBasedOffer;
  }
  const {
    offerIds
  } = getPromotionForSkuId(state, {
    skuId
  });
  const offersMap = getOffersMap(state);
  if (!offerIds || !offersMap || !terms) return null;
  const termMap = [terms, ...fallbackTerms];
  const offerIdsWithTerms = termMap.map(term => {
    return findOfferIdForTerm(state, {
      offerIds,
      offerTerms: term
    });
  });
  const selectedOfferId = offerIdsWithTerms.find(offerId => offerId) || null;
  return selectedOfferId;
};
export const getCrmSuiteOfferIdsForProductLevel = (state, {
  productLevel
}) => {
  const {
    promotions
  } = state;
  const crmSuiteOfferIds = promotions.crmSuiteOfferIds;
  if (!crmSuiteOfferIds || !Object.keys(crmSuiteOfferIds).length) {
    return {
      productOfferIds: [],
      quantityPackOfferIdsBySkuUuid: {},
      seatsOfferIds: []
    };
  }
  const {
    crmSuiteOfferIds: {
      [productLevel]: {
        productOfferIds,
        seatsOfferIds,
        quantityPackOfferIdsBySkuUuid
      }
    }
  } = promotions;
  return {
    productOfferIds,
    quantityPackOfferIdsBySkuUuid,
    seatsOfferIds
  };
};
export const getCrmSuiteQuantityPackOfferIdsForProductLevel = (state, {
  quantityPackSkuId,
  productLevel
}) => {
  const {
    apiName
  } = getProductForSkuId(state, {
    skuId: quantityPackSkuId
  });
  const skuUuid = getSkuUuidForSkuId(state, {
    skuId: quantityPackSkuId
  });
  const {
    quantityPackOfferIdsBySkuUuid
  } = getCrmSuiteOfferIdsForProductLevel(state, {
    productLevel
  });
  if (!Object.keys(quantityPackOfferIdsBySkuUuid).includes(apiName)) {
    return [];
  }
  const {
    [apiName]: quantityPackOfferIdsForSuiteProduct
  } = quantityPackOfferIdsBySkuUuid;
  if (!Object.keys(quantityPackOfferIdsForSuiteProduct).length || !Object.keys(quantityPackOfferIdsForSuiteProduct).includes(skuUuid)) {
    return [];
  }
  const {
    [skuUuid]: quantityPackUnitOfferIds
  } = quantityPackOfferIdsForSuiteProduct;
  return quantityPackUnitOfferIds;
};
export const getCrmSuiteSeatsOfferIdsForProductLevel = (state, {
  productLevel
}) => {
  const crmSuiteOfferIds = getCrmSuiteOfferIdsForProductLevel(state, {
    productLevel
  });
  return crmSuiteOfferIds.seatsOfferIds;
};
export const getCrmSuiteAvailableTerms = (state, {
  productLevel
}) => {
  const {
    productOfferIds
  } = getCrmSuiteOfferIdsForProductLevel(state, {
    productLevel
  });
  return getMultipleOfferInfo(state, {
    offerIds: productOfferIds
  });
};
export const getCrmSuiteOfferId = (state, {
  productLevel,
  terms = {
    commitment: ANNUAL,
    payment: ANNUAL
  },
  fallbackTerms = []
}) => {
  const {
    productOfferIds
  } = getCrmSuiteOfferIdsForProductLevel(state, {
    productLevel
  });
  return [terms, ...fallbackTerms].map(term => findOfferIdForTerm(state, {
    offerIds: productOfferIds,
    offerTerms: term
  })).find(offerId => offerId) || null;
};
export const getCrmSuiteQuantityPackOfferId = (state, {
  quantityPackSkuId,
  productLevel,
  terms = {
    commitment: ANNUAL,
    payment: ANNUAL
  },
  fallbackTerms = []
}) => {
  const quantityPackOfferIds = getCrmSuiteQuantityPackOfferIdsForProductLevel(state, {
    quantityPackSkuId,
    productLevel
  });
  return [terms, ...fallbackTerms].map(term => findOfferIdForTerm(state, {
    offerIds: quantityPackOfferIds,
    offerTerms: term
  })).find(offerId => offerId) || null;
};
export const getCrmSuiteSeatsOfferId = (state, {
  productLevel,
  terms = {
    commitment: ANNUAL,
    payment: ANNUAL
  },
  fallbackTerms = []
}) => {
  const seatOfferIds = getCrmSuiteSeatsOfferIdsForProductLevel(state, {
    productLevel
  });
  return [terms, ...fallbackTerms].map(term => findOfferIdForTerm(state, {
    offerIds: seatOfferIds,
    offerTerms: term
  })).find(offerId => offerId) || null;
};
export const getCrmSuiteLowestTermsOfferId = (state, {
  productLevel
}) => {
  const terms = {
    commitment: MONTHLY,
    payment: MONTHLY
  };
  const fallbackTerms = [{
    commitment: ANNUAL,
    payment: MONTHLY
  }, {
    commitment: ANNUAL,
    payment: ANNUAL
  }];
  if (!productLevel) {
    const errorMsg = 'Invalid productLevel passed to getCrmSuiteLowestTermsOfferId';
    console.error(errorMsg, {
      productLevel
    });
    Raven.captureMessage(errorMsg, {
      extra: {
        productLevel
      }
    });
    return null;
  }
  return getCrmSuiteOfferId(state, {
    productLevel,
    terms,
    fallbackTerms
  });
};
export const getCrmSuiteQuantityPackLowestTermsOfferId = (state, {
  quantityPackSkuId,
  productLevel
}) => {
  const terms = {
    commitment: MONTHLY,
    payment: MONTHLY
  };
  const fallbackTerms = [{
    commitment: ANNUAL,
    payment: MONTHLY
  }, {
    commitment: ANNUAL,
    payment: ANNUAL
  }];
  if (!productLevel) {
    const errorMsg = 'Invalid productLevel passed to getCrmSuiteQuantityPackLowestTermsOfferId';
    console.error(errorMsg, {
      productLevel
    });
    Raven.captureMessage(errorMsg, {
      extra: {
        productLevel
      }
    });
    return null;
  }
  return getCrmSuiteQuantityPackOfferId(state, {
    quantityPackSkuId,
    productLevel,
    terms,
    fallbackTerms
  });
};
export const getCrmSuiteSeatsLowestTermsOfferId = (state, {
  productLevel
}) => {
  const terms = {
    commitment: MONTHLY,
    payment: MONTHLY
  };
  const fallbackTerms = [{
    commitment: ANNUAL,
    payment: MONTHLY
  }, {
    commitment: ANNUAL,
    payment: ANNUAL
  }];
  if (!productLevel) {
    const errorMsg = 'Invalid productLevel passed to getCrmSuiteSeatsLowestTermsOfferId';
    console.error(errorMsg, {
      productLevel
    });
    Raven.captureMessage(errorMsg, {
      extra: {
        productLevel
      }
    });
    return null;
  }
  return getCrmSuiteSeatsOfferId(state, {
    productLevel,
    terms,
    fallbackTerms
  });
};

// If the query param is annual, always pick the second lowest terms offer id
// (low in this context means lower commitment and payment terms)
// Otherwise, pick the first available offer id
const offerIdForQueryParamTerm = (offerIds, queryParamTerm) => {
  if (queryParamTerm === ANNUAL && offerIds.length > 1) {
    return offerIds[1];
  } else if (offerIds.length) {
    return offerIds[0];
  }
  return null;
};
export const getOfferIdForQueryParamTerm = (state, {
  queryParamTerm,
  skuId
}) => {
  return offerIdForQueryParamTerm(getAvailableOfferIdsForSku(state, {
    skuId,
    limitToTwo: true
  }), queryParamTerm);
};
export const getCrmSuiteOfferIdFromQueryParam = (state, {
  productLevel,
  queryParamTerm
}) => {
  const {
    productOfferIds
  } = getCrmSuiteOfferIdsForProductLevel(state, {
    productLevel
  });
  return offerIdForQueryParamTerm(productOfferIds, queryParamTerm);
};
export const getCrmSuiteQuantityPackOfferIdFromQueryParam = (state, {
  quantityPackSkuId,
  productLevel,
  queryParamTerm
}) => {
  const quantityPackOfferIds = getCrmSuiteQuantityPackOfferIdsForProductLevel(state, {
    quantityPackSkuId,
    productLevel
  });
  return offerIdForQueryParamTerm(quantityPackOfferIds, queryParamTerm);
};
export const getCrmSuiteSeatsOfferIdFromQueryParam = (state, {
  productLevel,
  queryParamTerm
}) => {
  const seatOfferIds = getCrmSuiteSeatsOfferIdsForProductLevel(state, {
    productLevel
  });
  return offerIdForQueryParamTerm(seatOfferIds, queryParamTerm);
};
export const getProductQuantityPackOfferIds = (state, {
  apiName,
  versionNumber,
  quantityPackSkuUuid
}) => {
  var _quantityPackOfferIds;
  const {
    promotions
  } = state;
  const {
    offersById
  } = promotions;
  const {
    quantityPackOfferIdsBySkuUuid
  } = getProductPromotion(state, {
    apiName,
    versionNumber
  });
  const quantityPackOfferIdsForSkuUuid = (quantityPackOfferIdsBySkuUuid === null || quantityPackOfferIdsBySkuUuid === void 0 || (_quantityPackOfferIds = quantityPackOfferIdsBySkuUuid[quantityPackSkuUuid]) === null || _quantityPackOfferIds === void 0 ? void 0 : _quantityPackOfferIds.offerIds) || [];
  const sortedQuantityPackOfferIds = sortOfferIds(offersById, quantityPackOfferIdsForSkuUuid);
  return sortedQuantityPackOfferIds;
};
export const getProductQuantityPackOfferIdFromQueryParam = (state, {
  apiName,
  versionNumber,
  queryParamTerm,
  quantityPackSkuUuid
}) => {
  const quantityPackOfferIds = getProductQuantityPackOfferIds(state, {
    apiName,
    versionNumber,
    quantityPackSkuUuid
  });
  return offerIdForQueryParamTerm(quantityPackOfferIds, queryParamTerm);
};
export const getProductQuantityPackOfferId = (state, {
  apiName,
  versionNumber,
  quantityPackSkuUuid,
  terms = {
    commitment: ANNUAL,
    payment: ANNUAL
  },
  fallbackTerms = []
}) => {
  const quantityPackOfferIds = getProductQuantityPackOfferIds(state, {
    apiName,
    versionNumber,
    quantityPackSkuUuid
  });
  return [terms, ...fallbackTerms].map(term => findOfferIdForTerm(state, {
    offerIds: quantityPackOfferIds,
    offerTerms: term
  })).find(offerId => offerId) || null;
};
export const getProductQuantityPackLowestTermsOfferId = (state, {
  apiName,
  versionNumber,
  quantityPackSkuUuid
}) => {
  const terms = {
    commitment: MONTHLY,
    payment: MONTHLY
  };
  const fallbackTerms = [{
    commitment: ANNUAL,
    payment: MONTHLY
  }, {
    commitment: ANNUAL,
    payment: ANNUAL
  }];
  return getProductQuantityPackOfferId(state, {
    apiName,
    versionNumber,
    quantityPackSkuUuid,
    terms,
    fallbackTerms
  });
};
const validatedQueryParamTerm = ({
  queryParamTerm,
  offerTerms
}) => {
  if (!offerTerms || !offerTerms.length || offerTerms.some(terms => !terms)) {
    return;
  }
  const isValid = offerTerms.some(terms => terms.commitment === queryParamTerm || terms.payment === queryParamTerm);
  if (isValid) {
    if (queryParamTerm !== ONCE) return queryParamTerm;
  }
  return queryParamTerm === MONTHLY ? ANNUAL : MONTHLY;
};
export const getValidatedQueryParamTermForCrmSuite = (state, {
  productLevel,
  queryParamTerm
}) => {
  const {
    promotions
  } = state;
  if (!Object.keys(promotions).length) {
    return;
  }
  if (!hasUnbundledCrmSuiteOfferIdsInPromotions(state)) {
    return queryParamTerm;
  }
  return validatedQueryParamTerm({
    offerTerms: getCrmSuiteAvailableTerms(state, {
      productLevel
    }),
    queryParamTerm
  });
};
export const getCommonAvailableOfferIds = (state, {
  skuIds
}) => {
  if (!skuIds) return [];
  const skuIdsToUse = getHubProductVersionSkuIdsOrDefault(state, {
    skuIds
  });
  const isOnlySuite = getSkuIdsContainOnlyCRMSuite(state, {
    skuIds
  });
  if (isOnlySuite) {
    const suiteProductLevel = getCrmSuiteProductLevelForSkuIdsPreSeatsAndSeatsOnly(state, {
      skuIds
    });
    if (suiteProductLevel) {
      const {
        productOfferIds
      } = getCrmSuiteOfferIdsForProductLevel(state, {
        productLevel: suiteProductLevel
      });
      return productOfferIds;
    }
  }
  const offerIdsList = skuIdsToUse.map(skuId => getAvailableOfferIdsForSku(state, {
    skuId,
    limitToTwo: false
  }));
  if (!offerIdsList.length) return [];
  return offerIdsList[0].filter(offerId => offerIdsList.every(offerIds => offerIds.includes(offerId)));
};
const getCommonAvailableTermsHelper = (state, {
  skuIds
}) => {
  if (!skuIds.length) return [];
  const recurringSkuIds = skuIds.filter(skuId => {
    const product = getProductForSkuId(state, {
      skuId
    });
    return product && product.chargeType === RECURRING;
  });
  if (!recurringSkuIds.length) return [];
  const isOnlySuite = getSkuIdsContainOnlyCRMSuite(state, {
    skuIds: recurringSkuIds
  });
  const hasUnbundledCrmSuiteOfferIds = hasUnbundledCrmSuiteOfferIdsInPromotions(state);
  const offerIdsList = recurringSkuIds.map(skuId => isOnlySuite && hasUnbundledCrmSuiteOfferIds ? getCommonAvailableOfferIds(state, {
    skuIds: recurringSkuIds
  }) // will return the respective suite offer ids
  : getAvailableOfferIdsForSku(state, {
    skuId,
    limitToTwo: false
  }));
  const termsList = offerIdsList.map(offerIds => offerIds.map(offerId => getOfferInfo(state, {
    offerId
  })));
  const availableTerms = termsList[0].filter(term => termsList.every(terms => terms.some(termToCheck => term.payment === termToCheck.payment && term.commitment === termToCheck.commitment)));
  if (!availableTerms.length) {
    Raven.captureMessage('Failed to find common available terms for skus', {
      extra: {
        skuIds
      }
    });
  }
  return availableTerms;
};

/**
 * @deprecated Please use one of the POP hooks in usePurchaseOptions.ts or useBulkPurchaseOptions.ts
 */
export const getCommonAvailableTerms = (state, {
  skuIds
}) => {
  if (!skuIds.length) return [];
  const suiteProductLevel = getCrmSuiteProductLevelForSkuIdsPreSeatsAndSeatsOnly(state, {
    skuIds
  });
  const hasUnbundledCrmSuiteOfferIds = hasUnbundledCrmSuiteOfferIdsInPromotions(state);
  if (suiteProductLevel && hasUnbundledCrmSuiteOfferIds) {
    const availableTermsSuite = getCrmSuiteAvailableTerms(state, {
      productLevel: suiteProductLevel
    });
    const isOnlySuite = getSkuIdsContainOnlyCRMSuite(state, {
      skuIds
    });
    if (isOnlySuite) {
      return availableTermsSuite;
    }
    const nonSuiteSkuIds = skuIds.filter(skuId => !getSkuIdIsInCrmSuite(state, {
      skuId,
      suiteProductLevel
    }));
    const availableTermsNonSuite = getCommonAvailableTermsHelper(state, {
      skuIds: nonSuiteSkuIds
    });
    return availableTermsSuite.filter(term => availableTermsNonSuite.some(termToCheck => term.payment === termToCheck.payment && term.commitment === termToCheck.commitment));
  }
  return getCommonAvailableTermsHelper(state, {
    skuIds
  });
};

/**
 * @deprecated Please use one of the POP hooks in usePurchaseOptions.ts or useBulkPurchaseOptions.ts
 */
export const getCommonAllowedQueryParamTerm = (state, {
  skuIds,
  selectedTerm,
  defaultTerm = MONTHLY
}) => {
  const commonAvailableOfferTerms = getCommonAvailableTerms(state, {
    skuIds
  });

  // filter to annual or monthly terms only
  const commonAvailableTerms = commonAvailableOfferTerms.map(offer => offer.payment).filter(term => term !== ONCE);

  // if the selected term is common, use that
  // otherwise, use the first common available
  // default to monthly
  return commonAvailableTerms.includes(selectedTerm) ? selectedTerm : commonAvailableTerms && commonAvailableTerms.length ? commonAvailableTerms[0] : defaultTerm;
};
export const getLowestTermsOfferId = (state, {
  skuId
}) => getOfferId(state, {
  skuId,
  terms: {
    commitment: MONTHLY,
    payment: MONTHLY
  },
  fallbackTerms: [{
    commitment: ANNUAL,
    payment: MONTHLY
  }, {
    commitment: ANNUAL,
    payment: ANNUAL
  }]
});

// Returns the offer id with the highest discount. Used to calculate the cheapest monthly cost
// of a sku
export const getHighestTermsOfferId = (state, {
  skuId
}) => getOfferId(state, {
  skuId,
  terms: {
    commitment: ANNUAL,
    payment: ANNUAL
  },
  fallbackTerms: [{
    commitment: ANNUAL,
    payment: MONTHLY
  }, {
    commitment: MONTHLY,
    payment: MONTHLY
  }]
});

// For CRM Suite Professional and Enterprise where we want to treat additional units, add ons, and
// and services with single product offer ids, rather than the suite offer id. This is temporary
// until our backend can support multiple offer ids/discounts.
// Slack thread for context: https://hubspot.slack.com/archives/C824F0ZB5/p1618244650087300
export const getMatchingTermsOfferId = (state, {
  currentOfferId,
  skuId
}) => {
  let offerInfoTerms;
  if (typeof currentOfferId === 'number') {
    const offerInfo = getOfferInfo(state, {
      offerId: currentOfferId
    });
    offerInfoTerms = offerInfo ? {
      payment: offerInfo.payment,
      commitment: offerInfo.commitment
    } : undefined;
  }
  return getOfferId(state, {
    skuId,
    terms: offerInfoTerms,
    fallbackTerms: [{
      commitment: MONTHLY,
      payment: MONTHLY
    }, {
      commitment: ANNUAL,
      payment: ANNUAL
    }]
  });
};
export const getPromotionsDataIsEmpty = state => {
  const {
    promotions
  } = state;
  return Object.values(promotions).every(val => {
    if (!val || Object.keys(val).length === 0) return true;
    return false;
  });
};
const getMatchingHubOrBundleOfferIdForSkuId = (state, {
  skuId,
  allProductConfigurations,
  suiteProductLevel,
  queryParamTerm
}) => {
  const skuIdToUseForAdditionalMerchDiscounts = allProductConfigurations.map(({
    skuId: skuIdInConfig
  }) => skuIdInConfig).find(skuIdInConfig => {
    return getSkuIdIsOrHasHub(state, {
      skuId: skuIdInConfig
    });
  });
  const hasUnbundledCrmSuiteOfferIds = hasUnbundledCrmSuiteOfferIdsInPromotions(state);
  let currentOfferId = null;
  if (suiteProductLevel && hasUnbundledCrmSuiteOfferIds) {
    if (queryParamTerm) {
      currentOfferId = getCrmSuiteOfferIdFromQueryParam(state, {
        productLevel: suiteProductLevel,
        queryParamTerm
      });
    } else {
      currentOfferId = getCrmSuiteLowestTermsOfferId(state, {
        productLevel: suiteProductLevel
      });
    }
  } else if (skuIdToUseForAdditionalMerchDiscounts) {
    if (queryParamTerm) {
      currentOfferId = getOfferIdForQueryParamTerm(state, {
        queryParamTerm,
        skuId: skuIdToUseForAdditionalMerchDiscounts
      });
    } else {
      currentOfferId = getLowestTermsOfferId(state, {
        skuId: skuIdToUseForAdditionalMerchDiscounts
      });
    }
  }
  if (currentOfferId) {
    return getMatchingTermsOfferId(state, {
      currentOfferId,
      skuId
    });
  } else if (queryParamTerm) {
    return getOfferIdForQueryParamTerm(state, {
      queryParamTerm,
      skuId
    });
  }
  return getLowestTermsOfferId(state, {
    skuId
  });
};
const getOfferIdForCoreSeat = (state, {
  allProductConfigurations,
  skuId,
  queryParamTerm
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    productApiName: apiName
  } = skuMap[skuId];
  if (!isCoreSeatSku(apiName)) return null;

  // suiteProductLevel is set when there's an unbundled suite in `allProductConfigurations`
  const suiteProductLevel = getCrmSuiteProductLevelForProductConfigurations(state, {
    productConfigurations: allProductConfigurations
  });
  const hasUnbundledCrmSuiteOfferIds = hasUnbundledCrmSuiteOfferIdsInPromotions(state);

  // For unbundled suite, Core Seats need to get their offer ids from crmSuiteOfferIds within seatsOfferIds field in the promo response
  if (suiteProductLevel && hasUnbundledCrmSuiteOfferIds) {
    return queryParamTerm ? getCrmSuiteSeatsOfferIdFromQueryParam(state, {
      productLevel: suiteProductLevel,
      queryParamTerm
    }) : getCrmSuiteSeatsLowestTermsOfferId(state, {
      productLevel: suiteProductLevel
    });
  }

  // For any other scenario, Core Seats can be treated like add-ons where they should match the terms of whatever hubs or bundle skus they're purchased with
  return getMatchingHubOrBundleOfferIdForSkuId(state, {
    skuId,
    allProductConfigurations,
    suiteProductLevel,
    queryParamTerm
  });
};
const getOfferIdForQuantityPack = (state, {
  skuId,
  allProductConfigurations,
  queryParamTerm
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    skuUuid
  } = skuMap[skuId];
  const suiteProductLevel = getCrmSuiteProductLevelForProductConfigurations(state, {
    productConfigurations: allProductConfigurations
  });
  const hasUnbundledCrmSuiteOfferIds = hasUnbundledCrmSuiteOfferIdsInPromotions(state);
  const productVersion = getProductVersion(state, {
    skuId
  });
  if (!productVersion) throw new Error(`productVersion was undefined for skuId ${skuId}`);
  const {
    productApiName: apiName,
    versionNumber
  } = productVersion;

  // suiteProductLevel is set when we have an unbundled suite in `allProductConfigurations`, so this only applies to pre-seats & non-TB seats; we get the offer id from crmSuiteOfferIds, within quantityPackOfferIds field in the promo response
  if (suiteProductLevel && hasUnbundledCrmSuiteOfferIds) {
    return queryParamTerm ? getCrmSuiteQuantityPackOfferIdFromQueryParam(state, {
      quantityPackSkuId: skuId,
      productLevel: suiteProductLevel,
      queryParamTerm
    }) : getCrmSuiteQuantityPackLowestTermsOfferId(state, {
      quantityPackSkuId: skuId,
      productLevel: suiteProductLevel
    });
  }
  return queryParamTerm ? getProductQuantityPackOfferIdFromQueryParam(state, {
    apiName,
    versionNumber,
    quantityPackSkuUuid: skuUuid,
    queryParamTerm
  }) : getProductQuantityPackLowestTermsOfferId(state, {
    apiName,
    versionNumber,
    quantityPackSkuUuid: skuUuid
  });
};

/**
 * Determines the best offer id for a sku in a list of product configurations.
 */
export function getOfferIdForSkuInProductConfigurations(state, {
  allProductConfigurations,
  productConfiguration,
  queryParamTerm
}) {
  const isPromotionsDataEmpty = getPromotionsDataIsEmpty(state);
  if (isPromotionsDataEmpty) return null;
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    skuType
  } = skuMap[productConfiguration.skuId];
  const productVersion = getProductVersion(state, {
    skuId: productConfiguration.skuId
  });
  if (!productVersion) throw new Error(`productVersion was undefined for skuId ${productConfiguration.skuId}`);
  const {
    skuId
  } = productVersion;
  const {
    apiName,
    chargeType
  } = getProductForSkuId(state, {
    skuId
  });
  const isSeatsPilot = getIsUngatedForSeats(state);
  const suiteProductLevel = getCrmSuiteProductLevelForProductConfigurations(state, {
    productConfigurations: allProductConfigurations
  });
  const hasUnbundledCrmSuiteOfferIds = hasUnbundledCrmSuiteOfferIdsInPromotions(state);

  /**
   * Product based offers are discounts for a specific product that has a non-recurring charge type. Each product base offer is unique for that specific sku. E.g. onboarding skus used to have a product base offer that was unique to that sku.
   * See: https://docs.google.com/document/d/13G_tvK3a0gw2fK6PM-0M61Z-rMo3tubg0VrbCyiq1-w/edit?usp=sharing
   */
  const productBasedOffer = chargeType === NON_RECURRING && getProductBasedOfferId(state, {
    skuId
  });
  if (productBasedOffer) return productBasedOffer;
  function validOfferIdOrLogError(offerId, context) {
    if (!offerId) {
      const err = new OfferIdNotFoundError('Failed to find offer ID for sku', Object.assign({
        allProductConfigurations,
        productConfiguration,
        queryParamTerm,
        skuId,
        apiName,
        chargeType,
        isSeatsPilot,
        suiteProductLevel
      }, context));
      Raven.captureException(err, {
        tags: {
          version: '2'
        },
        extra: Object.assign({}, err.getContext())
      });
    }
    return offerId;
  }

  // For Sales/Service Seats in unbundled suite, we want to get the offer id from the crmSuiteOfferIds within the seatsOfferIds field in the promo response
  const isUnbundledCrmSuiteSalesOrServiceSeat = isSeatsPilot && suiteProductLevel && hasUnbundledCrmSuiteOfferIds && getIsCrmSuiteSalesOrServiceSkuProEnt(state, {
    skuId,
    suiteProductLevel
  });
  if (isUnbundledCrmSuiteSalesOrServiceSeat) {
    return validOfferIdOrLogError(queryParamTerm ? getCrmSuiteSeatsOfferIdFromQueryParam(state, {
      productLevel: suiteProductLevel,
      queryParamTerm
    }) : getCrmSuiteSeatsLowestTermsOfferId(state, {
      productLevel: suiteProductLevel
    }), {
      isUnbundledCrmSuiteSalesOrServiceSeat
    });
  }
  const isAddonOrService = getSkuIdIsAddonOrService(state, {
    skuId
  });
  if (isAddonOrService) {
    return validOfferIdOrLogError(getMatchingHubOrBundleOfferIdForSkuId(state, {
      skuId,
      allProductConfigurations,
      suiteProductLevel,
      queryParamTerm
    }), {
      isAddonOrService
    });
  }
  const isCoreSeat = isCoreSeatSku(apiName);
  if (isCoreSeat) {
    return validOfferIdOrLogError(getOfferIdForCoreSeat(state, {
      skuId,
      allProductConfigurations,
      queryParamTerm
    }), {
      isCoreSeat
    });
  }
  const isQuantityPack = skuType === QUANTITY_PACK;
  if (isQuantityPack) {
    return validOfferIdOrLogError(getOfferIdForQuantityPack(state, {
      skuId: productConfiguration.skuId,
      allProductConfigurations,
      queryParamTerm
    }), {
      isQuantityPack
    });
  }
  let offerIdToUse = null;

  // Other PRODUCT_VERSION under a unbundled suite, get the offer id from the crmSuiteOfferIds within the seatsOfferIds field in the promo response
  if (!offerIdToUse && suiteProductLevel && hasUnbundledCrmSuiteOfferIds) {
    offerIdToUse = queryParamTerm ? getCrmSuiteOfferIdFromQueryParam(state, {
      productLevel: suiteProductLevel,
      queryParamTerm
    }) : getCrmSuiteLowestTermsOfferId(state, {
      productLevel: suiteProductLevel
    });
  }
  if (!offerIdToUse) {
    offerIdToUse = queryParamTerm ? getOfferIdForQueryParamTerm(state, {
      skuId,
      queryParamTerm
    }) : getLowestTermsOfferId(state, {
      skuId
    });
  }
  return validOfferIdOrLogError(offerIdToUse);
}

/**
 * Formats the offer info to the terms needed for the purchase order generation service
 */
export const offerInfoToPurchaseRequestTerms = offerInfo => {
  var _offerInfo$commitment;
  return {
    commitmentTerm: ((_offerInfo$commitment = offerInfo.commitment) === null || _offerInfo$commitment === void 0 ? void 0 : _offerInfo$commitment.toLowerCase()) === 'monthly' ? 'MONTHLY' : 'ANNUAL',
    paymentTerm: offerInfo.payment.toLowerCase() === 'monthly' ? 'MONTHLY' : 'ANNUAL'
  };
};

/**
 * Gets the purchase request terms for a given list of product configurations and a query param term.
 * Used for sending to the purchase order generation (POG) service.
 */
export const getPurchaseRequestTermsFromProductConfigurations = (state, {
  productConfigurations,
  queryParamTerm
}) => {
  const {
    productData: {
      rawProductList
    }
  } = state;
  const isUngatedForSeats = getIsUngatedForSeats(state);
  const recurringProductConfigurations = getFilteredProductConfigurationsByChargeType(rawProductList, productConfigurations, RECURRING);
  const availableTerms = getCommonAvailableTerms(state, {
    skuIds: recurringProductConfigurations.map(productConfiguration => productConfiguration.skuId)
  });

  // if a seats customer already has a subscription, there will only be one possible term
  if (availableTerms.length === 1) {
    return offerInfoToPurchaseRequestTerms(availableTerms[0]);
  }

  // if you are buying only 1 thing, and we already own that 1 thing, use the same terms as in inventory
  if (productConfigurations.length === 1) {
    const {
      skuId
    } = productConfigurations[0];
    const currentTermForOwnedProduct = getCurrentTermFromProductInventory(state, {
      skuId
    });
    if (currentTermForOwnedProduct) {
      const currentPaymentTerm = currentTermForOwnedProduct.paymentTerm === 12 ? ANNUAL : MONTHLY;
      const currentCommitmentTerm = currentTermForOwnedProduct.commitmentTerm === 12 ? ANNUAL : MONTHLY;

      // Check for exact term match. This handles the edge case for pre-seats that are not on single subscription
      const matchingTerm = availableTerms.find(offer => offer.payment === currentPaymentTerm && offer.commitment === currentCommitmentTerm);
      if (matchingTerm) {
        return offerInfoToPurchaseRequestTerms(matchingTerm);
      }

      // Fallback to finding the matching payment term
      const matchingPaymentTerm = availableTerms.find(offer => offer.payment === currentPaymentTerm);
      if (matchingPaymentTerm) {
        return offerInfoToPurchaseRequestTerms(matchingPaymentTerm);
      }
    }
  }

  // If you are buying something that has terms matching the current subscription
  // terms, use the current subscription terms. This check is necessary to satisfy
  // the case where there is a current subscription but the product being purchased
  // has multiple term options (MCMP loophole).
  const currentSubscriptionTerms = getAllTermsInProductInventory(state);
  if (currentSubscriptionTerms.length === 1 && isUngatedForSeats) {
    if (availableTerms.some(term => term.commitment === currentSubscriptionTerms[0].commitmentTerm && term.payment === currentSubscriptionTerms[0].paymentTerm)) {
      return offerInfoToPurchaseRequestTerms({
        commitment: currentSubscriptionTerms[0].commitmentTerm,
        payment: currentSubscriptionTerms[0].paymentTerm,
        discount: 0 // adding to satisfy type
      });
    }
  }

  // find the term that matches the queryParamTerm (payment term)
  const matchingTerms = queryParamTerm ? availableTerms.find(offer => offer.payment === queryParamTerm) : undefined;
  if (matchingTerms) {
    return offerInfoToPurchaseRequestTerms(matchingTerms);
  }

  // if there are no matching terms, return undefined and rely on POG to then determine the terms, not the FE
  return undefined;
};